/**
 * Cluster detail page component for viewing detailed information about a Kubernetes cluster.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import {
  getCluster,
  KubernetesCluster,
  getProject,
  OpenStackProject,
  getCloud,
  OpenStackCloud,
  OpenStackVM,
  refreshProjectVMs,
} from '../api/openstack';
import { RefreshIcon } from '../components/Icons';
import { useWebSocket } from '../contexts/WebSocketContext';
import { logError } from '../utils/errorLogging';
import { getErrorWithContext } from '../utils/errorMessages';
import './ClusterDetailPage.css';

export const ClusterDetailPage: React.FC = () => {
  const { projectId, clusterName } = useParams<{ projectId: string; clusterName: string }>();
  const navigate = useNavigate();
  const [cluster, setCluster] = useState<KubernetesCluster | null>(null);
  const [project, setProject] = useState<OpenStackProject | null>(null);
  const [cloud, setCloud] = useState<OpenStackCloud | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof OpenStackVM | ''>('vm_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  const { isConnected, on, off } = useWebSocket();

  const loadCluster = async () => {
    if (!projectId || !clusterName) return;
    
    try {
      setError(null);
      const clusterData = await getCluster(projectId, clusterName);
      setCluster(clusterData);
    } catch (err: any) {
      logError('Failed to load cluster', err);
      setError(getErrorWithContext(err, 'load cluster'));
    } finally {
      setLoading(false);
    }
  };

  const loadProjectDetails = async () => {
    if (!projectId) return;
    
    try {
      const projectData = await getProject(projectId);
      setProject(projectData);
      
      // Load cloud information to get cloud_name for breadcrumb
      if (projectData.cloud_id) {
        try {
          const cloudData = await getCloud(projectData.cloud_id);
          setCloud(cloudData);
        } catch (err: any) {
          logError('Failed to load cloud', err);
          // Don't fail the whole page if cloud load fails
        }
      }
    } catch (err: any) {
      logError('Failed to load project', err);
      // Don't fail the whole page if project load fails
    }
  };

  const handleRefresh = async () => {
    if (!projectId) return;
    
    setRefreshing(true);
    setError(null);
    setSuccess(null);
    
    try {
      await refreshProjectVMs(projectId);
      await loadCluster();
      setSuccess('Cluster refreshed successfully');
    } catch (err: any) {
      logError('Failed to refresh', err);
      setError(getErrorWithContext(err, 'refresh cluster'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleSort = (column: keyof OpenStackVM) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const formatBytes = (mb: number): string => {
    if (mb < 1024) return `${mb} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status?: string): string => {
    if (!status) return 'var(--text-secondary)';
    const statusLower = status.toLowerCase();
    if (statusLower === 'active') return 'var(--success-color)';
    if (statusLower === 'shutoff' || statusLower === 'stopped') return 'var(--text-secondary)';
    if (statusLower === 'error') return 'var(--error-color)';
    return 'var(--text-color)';
  };

  const isControlPlane = (vm: OpenStackVM): boolean => {
    return vm.vm_name?.toLowerCase().includes('-controlplane-') ?? false;
  };

  const isWorker = (vm: OpenStackVM): boolean => {
    return vm.vm_name?.toLowerCase().includes('-worker-') ?? false;
  };

  useEffect(() => {
    if (projectId && clusterName) {
      setLoading(true);
      loadCluster();
    }
    if (projectId) {
      loadProjectDetails();
    }
  }, [projectId, clusterName]);

  // Register WebSocket event handlers for VM updates
  useEffect(() => {
    if (!projectId) return;

    const handleVMListUpdate = async (message: { event: string; data?: any }) => {
      const { project_id } = message.data || {};
      
      if (project_id === projectId) {
        // Reload cluster when VMs are updated
        await loadCluster();
      }
    };

    const handleVMUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, project_id } = message.data || {};
      
      if (project_id === projectId && event_type === 'updated') {
        // Reload cluster when VMs are updated
        await loadCluster();
      }
    };

    const unsubscribeVMList = on('vm_list_update', handleVMListUpdate);
    const unsubscribeVM = on('vm_update', handleVMUpdate);

    return () => {
      unsubscribeVMList();
      unsubscribeVM();
    };
  }, [projectId, on, off]);

  if (loading) {
    return (
      <Layout>
        <div className="cluster-detail-page">
          <div className="cluster-detail-container">
            <div className="loading-message">Loading cluster...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!cluster) {
    return (
      <Layout>
        <div className="cluster-detail-page">
          <div className="cluster-detail-container">
            <div className="error-message">
              {error || 'Cluster not found'}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Sort VMs
  const sortedVMs = [...cluster.vms].sort((a, b) => {
    // Group by type: control planes first, then workers
    const aIsControlPlane = isControlPlane(a);
    const bIsControlPlane = isControlPlane(b);
    const aIsWorker = isWorker(a);
    const bIsWorker = isWorker(b);
    
    // Control planes come first
    if (aIsControlPlane && !bIsControlPlane) return -1;
    if (!aIsControlPlane && bIsControlPlane) return 1;
    
    // Workers come second
    if (aIsWorker && !bIsWorker) return -1;
    if (!aIsWorker && bIsWorker) return 1;
    
    // Within same type, sort by selected column
    if (sortColumn) {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;
      
      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    }
    
    return 0;
  });

  // Calculate totals
  const totalCores = cluster.vms.reduce((sum, vm) => sum + (vm.cores || 0), 0);
  const totalRAM = cluster.vms.reduce((sum, vm) => sum + (vm.ram_mb || 0), 0);
  const totalDisk = cluster.vms.reduce((sum, vm) => sum + (vm.disk_gb || 0), 0);

  // Check if control planes or workers have inactive nodes
  const controlPlanes = cluster.vms.filter(isControlPlane);
  const workers = cluster.vms.filter(isWorker);
  const hasInactiveControlPlanes = controlPlanes.some(
    (vm) => !vm.status || vm.status.toLowerCase() !== 'active'
  );
  const hasInactiveWorkers = workers.some(
    (vm) => !vm.status || vm.status.toLowerCase() !== 'active'
  );

  return (
    <Layout>
      <div className="cluster-detail-page">
        <div className="cluster-detail-container">
          {/* Breadcrumb */}
          <div className="cluster-breadcrumb">
            <Link to="/projects">Projects</Link>
            <span className="cluster-breadcrumb-separator">/</span>
            {project && cloud && (
              <>
                <Link to={`/projects/${projectId}`}>
                  {cloud.cloud_name || project.project_name || projectId}
                </Link>
                <span className="cluster-breadcrumb-separator">/</span>
              </>
            )}
            <span className="cluster-breadcrumb-current">{cluster.cluster_name}</span>
          </div>

          {/* Header */}
          <div className="cluster-detail-header">
            <div className="cluster-detail-title">
              <h1>{cluster.cluster_name}</h1>
            </div>
            <div className="cluster-detail-actions">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="btn-refresh"
                title="Refresh cluster"
              >
                <RefreshIcon />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              {success}
            </div>
          )}

          {/* Cluster Overview Section */}
          <div className="cluster-overview-section">
            <h2>Cluster Overview</h2>
            <div className="cluster-overview-grid">
              <div className="cluster-overview-card">
                <h3>VMs</h3>
                <div className="cluster-overview-value">{cluster.total_vms}</div>
              </div>
              <div className={`cluster-overview-card ${hasInactiveControlPlanes ? 'cluster-overview-card-error' : ''}`}>
                <h3>Control Planes</h3>
                <div className="cluster-overview-value">{cluster.control_plane_count}</div>
              </div>
              <div className={`cluster-overview-card ${hasInactiveWorkers ? 'cluster-overview-card-error' : ''}`}>
                <h3>Workers</h3>
                <div className="cluster-overview-value">{cluster.worker_count}</div>
              </div>
            </div>
            <div className="cluster-overview-grid">
              <div className="cluster-overview-card">
                <h3>Cores</h3>
                <div className="cluster-overview-value">{totalCores}</div>
              </div>
              <div className="cluster-overview-card">
                <h3>Memory</h3>
                <div className="cluster-overview-value">{Math.round(totalRAM / 1024)} GB</div>
              </div>
              <div className="cluster-overview-card">
                <h3>Disk</h3>
                <div className="cluster-overview-value">{Math.round(totalDisk)} GB</div>
              </div>
            </div>
          </div>

          {/* VM List Section */}
          <div className="cluster-vms-section">
            <h2>Virtual Machines ({cluster.vms.length})</h2>
            {cluster.vms.length === 0 ? (
              <div className="empty-state">No VMs in this cluster</div>
            ) : (
              <div className="cluster-vms-table-container">
                <table className="cluster-vms-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('vm_name')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSort('vm_name');
                          }
                        }}
                        aria-label="Sort by name"
                      >
                        Name
                        {sortColumn === 'vm_name' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('status')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSort('status');
                          }
                        }}
                        aria-label="Sort by status"
                      >
                        Status
                        {sortColumn === 'status' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('cores')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSort('cores');
                          }
                        }}
                        aria-label="Sort by cores"
                      >
                        Cores
                        {sortColumn === 'cores' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('ram_mb')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSort('ram_mb');
                          }
                        }}
                        aria-label="Sort by RAM"
                      >
                        RAM
                        {sortColumn === 'ram_mb' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('disk_gb')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSort('disk_gb');
                          }
                        }}
                        aria-label="Sort by disk"
                      >
                        Disk
                        {sortColumn === 'disk_gb' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th>Created By</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedVMs.map((vm) => (
                      <tr
                        key={vm.id}
                        className="cluster-vm-row"
                        onClick={() => navigate(`/projects/${projectId}/vms/${vm.vm_id}`)}
                      >
                        <td>
                          <span className="vm-type-badge">
                            {isControlPlane(vm) ? 'Control Plane' : isWorker(vm) ? 'Worker' : 'Unknown'}
                          </span>
                        </td>
                        <td className="vm-name">{vm.vm_name || vm.vm_id}</td>
                        <td>
                          <span
                            className="vm-status"
                            style={{ color: getStatusColor(vm.status) }}
                          >
                            {vm.status || 'UNKNOWN'}
                          </span>
                        </td>
                        <td>{vm.cores ?? 'N/A'}</td>
                        <td>{vm.ram_mb ? formatBytes(vm.ram_mb) : 'N/A'}</td>
                        <td>{vm.disk_gb ? `${vm.disk_gb} GB` : 'N/A'}</td>
                        <td>{vm.created_by_user_name?.trim() || 'N/A'}</td>
                        <td>{formatDate(vm.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
